import React, { useState } from "react";
import { Check, Loader2, ArrowRight } from "lucide-react";
import BookChatCTA from "./BookChatCTA";
import { api, formatApiError } from "../../api";
import { useContent } from "../../content/ContentContext";

const initial = { name: "", organisation: "", email: "", phone: "", help_with: "", message: "", website: "" };

export const ContactSection = ({ calendlyUrl }) => {
  const c = useContent("contact");
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please share your name";
    if (!form.email.trim()) e.email = "Please share your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "That email doesn't look right";
    if (!form.message.trim()) e.message = "A short note helps me prepare";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      setServerError(formatApiError(err, "Something went wrong. Please try again or email directly."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="sc-section">
      <div className="sc-container">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <span className="sc-eyebrow">{c.eyebrow}</span>
            <h2 className="sc-h2 mt-3" data-testid="contact-heading">
              {c.heading_pre}{" "}
              <em className="not-italic">{c.heading_em}</em>
            </h2>
            <p className="sc-lede mt-5">{c.lede}</p>
            <div className="mt-7">
              <BookChatCTA calendlyUrl={calendlyUrl} testId="contact-book-chat-btn" />
            </div>
            <p className="mt-6 sc-body">
              {c.secondary_note}
              <span className="block mt-1 text-[12px] text-muteink/70">{c.calendly_note}</span>
            </p>
          </div>

          <div className="md:col-span-7">
            <div className="bg-white rounded-2xl border border-blush-200/70 p-8 md:p-10 shadow-[0_20px_50px_-25px_rgba(180,43,106,0.18)]">
              {submitted ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="mx-auto w-12 h-12 rounded-full bg-blush-100 flex items-center justify-center">
                    <Check className="text-magenta" size={20} />
                  </div>
                  <p className="font-serif text-2xl md:text-3xl text-ink mt-5">{c.success_heading}</p>
                  <p className="sc-body mt-3 max-w-sm mx-auto">{c.success_body}</p>
                  <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" className="sc-btn-secondary mt-6" data-testid="contact-success-book-chat-btn">
                    Book a 20 minute chat
                  </a>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate data-testid="contact-form" className="space-y-7">
                  <input type="text" name="website" value={form.website} onChange={update("website")} tabIndex={-1} autoComplete="off" aria-hidden="true"
                         style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="name" className="sc-label">Name</label>
                      <input id="name" type="text" value={form.name} onChange={update("name")} className="sc-input" required autoComplete="name" data-testid="contact-name-input" />
                      {errors.name && <p className="text-magenta text-xs mt-1.5">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="organisation" className="sc-label">Organisation</label>
                      <input id="organisation" type="text" value={form.organisation} onChange={update("organisation")} className="sc-input" autoComplete="organization" data-testid="contact-organisation-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div>
                      <label htmlFor="email" className="sc-label">Email</label>
                      <input id="email" type="email" value={form.email} onChange={update("email")} className="sc-input" required autoComplete="email" data-testid="contact-email-input" />
                      {errors.email && <p className="text-magenta text-xs mt-1.5">{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="phone" className="sc-label">Phone <span className="normal-case tracking-normal text-muteink/70">(optional)</span></label>
                      <input id="phone" type="tel" value={form.phone} onChange={update("phone")} className="sc-input" autoComplete="tel" data-testid="contact-phone-input" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="help_with" className="sc-label">How can Rochelle help?</label>
                    <input id="help_with" type="text" value={form.help_with} onChange={update("help_with")} className="sc-input" placeholder="e.g. appeal management, donor strategy, extra capacity" data-testid="contact-help-input" />
                  </div>
                  <div>
                    <label htmlFor="message" className="sc-label">Message</label>
                    <textarea id="message" rows={4} value={form.message} onChange={update("message")} className="sc-input resize-none" required data-testid="contact-message-input" />
                    {errors.message && <p className="text-magenta text-xs mt-1.5">{errors.message}</p>}
                  </div>
                  {serverError && <p className="text-magenta text-sm" data-testid="contact-server-error">{serverError}</p>}
                  <div className="pt-2">
                    <button type="submit" disabled={submitting} className="sc-btn-secondary disabled:opacity-70 disabled:cursor-not-allowed" data-testid="contact-submit-btn">
                      {submitting ? (<><Loader2 size={16} className="animate-spin" />Sending…</>) : (<>Send message<ArrowRight size={16} /></>)}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
